import { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "./profile.css";
import ProductItem from "../components/ProductItem";
import api from "./api";
import Icons from "./Icons";
import CuruChatsPost from "./CuruChatsPost";
import { FollowProvider } from "../context/FollowContext";

const OtherUserProfile = () => {
  const [profile, setProfile] = useState(null);
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentUserId, setCurrentUserId] = useState(null);
  const [userLoaded, setUserLoaded] = useState(false);
  const [isFollowing, setIsFollowing] = useState(false);
  const [isUpdatingFollow, setIsUpdatingFollow] = useState(false);
  const [reviewsToShow, setReviewsToShow] = useState(4);
  const [posts, setPosts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [visibleCount, setVisibleCount] = useState(2);

  const navigate = useNavigate();
  const location = useLocation();
  const user = location.state?.user;
  const isOwnProfile = currentUserId === user?.id;

  useEffect(() => {
    const fetchCurrentUser = async () => {
      try {
        const res = await api.get("/profile");
        setCurrentUserId(res.data.id);
      } catch (err) {
        console.error("Failed to fetch current user", err);
      } finally {
        setUserLoaded(true);
      }
    };

    const fetchPosts = async () => {
      setIsLoading(true);
      try {
        const res = await api.get("/comunity/post");
        const filteredPosts = res.data.filter(
          (post) => post.user?.id === user.id
        );
        setPosts(filteredPosts);
      } catch (error) {
        console.error("Error fetching posts:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCurrentUser();
    if (user?.id) fetchPosts();
  }, [user?.id]);

  useEffect(() => {
    if (!user?.id) return;

    const fetchProfile = async () => {
      const token = localStorage.getItem("token");
      if (!token) {
        setError("No token found. Please log in again.");
        setLoading(false);
        navigate("/");
        return;
      }

      try {
        const response = await fetch(
          `https://dev-api.curu.com.au/api/follower/${user.id}/profile`,
          { headers: { Authorization: `Bearer ${token}` } }
        );

        if (!response.ok)
          throw new Error(
            `Failed to fetch profile data: ${response.statusText}`
          );

        const data = await response.json();
        setProfile(data);
        setIsFollowing(data.follow);
      } catch (error) {
        setError("Failed to load profile. Please try again later.");
        console.error("Error fetching profile:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchProfile();
  }, [user?.id, navigate]);

  useEffect(() => {
    const fetchProfileData = async () => {
      if (!user?.id) return;

      const token = localStorage.getItem("token");
      if (!token) {
        console.error("No token found.");
        return;
      }

      try {
        const [reviewsRes, ratingsRes] = await Promise.all([
          fetch(`https://dev-api.curu.com.au/api/follower/${user.id}/review`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
          fetch(`https://dev-api.curu.com.au/api/follower/${user.id}/rate`, {
            headers: { Authorization: `Bearer ${token}` },
          }),
        ]);

        if (!reviewsRes.ok || !ratingsRes.ok) {
          throw new Error("Error fetching reviews or ratings");
        }

        const reviewsData = await reviewsRes.json();
        const ratingsData = await ratingsRes.json();

        const mergedData = reviewsData.map((review) => {
          const rating = ratingsData.find((r) => r.id === review.id);
          return {
            ...review,
            rate: rating?.rate ?? review.rate ?? 0,
            rate_count: rating?.rate_count ?? review.rate_count ?? 0,
            user_reviews:
              review.user_reviews?.map((ur) => ({
                id: ur.id,
                text: ur.text,
                rate: parseFloat(ur.rate) || 0,
                created_ago: ur.created_ago,
                like_count: ur.like_count,
                is_liked: ur.is_liked,
                author: ur.author,
                photos: ur.photos.map((p) => ({ id: p.id, url: p.photo })),
              })) || [],
          };
        });

        const totalReviews = mergedData.reduce((count, review) => {
          return count + (review.user_reviews?.length || 0);
        }, 0);

        setProfile((prev) => ({ ...prev, review_count: totalReviews }));
        setReviews(mergedData);
      } catch (error) {
        console.error("Failed to load reviews and ratings:", error);
        setError("Failed to load reviews and ratings.");
      }
    };

    fetchProfileData();
  }, [user?.id]);

  useEffect(() => {
    if (profile?.is_following !== undefined) {
      setIsFollowing(profile.is_following);
    }
  }, [profile]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [user?.id]);

  const handleFollowToggle = async () => {
    setIsUpdatingFollow(true);
    const token = localStorage.getItem("token");
    if (!token) {
      setError("No token found. Please log in again.");
      navigate("/");
      return;
    }

    try {
      const newFollowState = !profile.follow;
      const res = await api.put(
        `https://dev-api.curu.com.au/api/follower/${user.id}/follow`,
        { follow: newFollowState },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setIsFollowing(res.data.follow);
    } catch (error) {
      console.error("Error toggling follow:", error);
      setError("Could not update follow status.");
    } finally {
      setIsUpdatingFollow(false);
    }
  };

  const mappedReviews = reviews.filter(
    (review) => review.rate || review.user_reviews.length > 0
  );

  const handleProfileClick = (user) => {
    if (!user || !user.id) return;
    navigate(`/profile/${user.id}`, { state: { user } });
  };

  if (loading) return <p>Loading profile...</p>;
  if (error) return <p className="error-message">{error}</p>;

  return (
    <div className="profile-page">
      <div className="profile-header">
        <div className="profile-banner">
          <div className="profile-picture-container">
            {profile?.photo ? (
              <img
                src={profile.photo}
                alt={profile.name || "Profile"}
                className="profile-picture"
              />
            ) : (
              <div className="profile-picture">
                <Icons.Profile />
              </div>
            )}
            <div className="level-badge">LVL {profile.level?.id || "N/A"}</div>
          </div>

          <div className="profile-info-container">
            <div className="profile-info">
              <h1>
                {profile.first_name} {profile.last_name || ""}
              </h1>
              <p>{profile.username}</p>
            </div>

            {profile.skin_type && profile.skin_type.name !== "Not set" && (
              <div className="other-user-skin-info-grid">
                {[
                  "skin_type",
                  "skin_undertone",
                  "skin_sensitive_type",
                  "skin_tone",
                  "skin_concerns",
                ].map((key) => {
                  const value =
                    key === "skin_concerns"
                      ? profile.skin_concerns?.length > 0
                        ? profile.skin_concerns
                        : "None"
                      : profile[key]?.name || "Not set";

                  if (value === "None" || value === "Not set") return null;

                  return (
                    <div
                      className={`other-user-skin-box other-user-skin-box-${key}`}
                      key={key}
                    >
                      <div className="other-user-skin-value-container">
                        <div className="other-user-skin-value">
                          {key === "skin_concerns"
                            ? value.map((concern, index) => (
                                <span key={concern.id}>
                                  {concern.name}
                                  {index < value.length - 1 && (
                                    <span>&nbsp;•&nbsp;</span>
                                  )}
                                </span>
                              ))
                            : value}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>

          <div className="follower-info-container">
            <div className="follower-info">
              <div className="follower-item">
                <strong>{profile.followers_count || "0"}</strong>
                <span>Followers</span>
              </div>
              <div className="follower-item">
                <strong>{mappedReviews.length || "0"}</strong>
                <span>Reviews</span>
              </div>
            </div>
            <div>
              <button
                className="edit-profile-button"
                onClick={handleFollowToggle}
                disabled={
                  loading || isOwnProfile || isUpdatingFollow || !userLoaded
                }
                title={isOwnProfile ? "Unable to follow user." : undefined}
              >
                {isFollowing ? "Unfollow" : "Follow"}
              </button>
            </div>
          </div>
        </div>
      </div>

      <div>
        <div className="reviews-heading">
          Reviews{" "}
          <span className="products-count">
            ({mappedReviews.length} products)
          </span>
        </div>
        {mappedReviews.length === 0 ? (
          <p>No reviews yet.</p>
        ) : (
          <>
            <div className="product-grid-view">
              <div className="row row-cols-1 row-cols-sm-2 row-cols-lg-3 row-cols-xl-4 g-4 mt-3">
                {mappedReviews.slice(0, reviewsToShow).map((item) => (
                  <div key={item.id} className="col">
                    <ProductItem item={item} starsAtBottom={true} />
                  </div>
                ))}
              </div>
            </div>
            {reviewsToShow < mappedReviews.length && (
              <div className="load-more-container">
                <button
                  className="load-more-btn"
                  onClick={() => setReviewsToShow(reviewsToShow + 4)}
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )}
      </div>

      <FollowProvider>
        <div className="posts-section">
          <div className="posts-heading">
            Posts{" "}
            {!isLoading && (
              <span className="products-count">
                (
                {posts.length === 0
                  ? "No posts"
                  : `${posts.length} post${posts.length > 1 ? "s" : ""}`}
                )
              </span>
            )}
          </div>
          <div className="posts-container">
            {isLoading ? (
              <p>Loading posts...</p>
            ) : posts.length > 0 ? (
              <>
                {posts
                  .sort(
                    (a, b) => new Date(b.created_at) - new Date(a.created_at)
                  )
                  .slice(0, visibleCount)
                  .map((post) => (
                    <CuruChatsPost
                      key={post.id}
                      post={post}
                      onProfileClick={handleProfileClick}
                      onDeleteSuccess={() =>
                        setPosts((prev) => prev.filter((p) => p.id !== post.id))
                      }
                    />
                  ))}
                {visibleCount < posts.length && (
                  <div className="text-center my-4">
                    <button
                      className="submit-button"
                      onClick={() => setVisibleCount((prev) => prev + 2)}
                    >
                      Load More
                    </button>
                  </div>
                )}
              </>
            ) : (
              <p>No posts found.</p>
            )}
          </div>
        </div>
      </FollowProvider>
    </div>
  );
};

export default OtherUserProfile;
